import {
  keepPreviousData,
  type QueryKey,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import type {
  AdditionalFaq,
  AdditionalFaqList,
  KnowledgeBaseView,
  KnowledgeQuestionDetail,
  PaginatedKnowledgeQuestions,
  KnowledgeQuestionSummary,
} from '../../types/knowledgeQuestions';
import { api, apiEndpoints } from '../../utils/apiService';
import { isChecking } from './knowledgeModel';

export const LIST_PAGE_SIZE = 50;
/** While a conflict check runs; checks finish in seconds. */
export const CHECK_POLL_MS = 2_000;

export async function readError(response: Response | null, fallback: string) {
  if (!response) return fallback;
  const body = (await response.json().catch(() => null)) as {
    detail?: string;
    question?: string[];
    answer?: string[];
    message?: string[];
    note?: string[];
  } | null;
  return (
    body?.detail ||
    body?.question?.[0] ||
    body?.answer?.[0] ||
    body?.message?.[0] ||
    body?.note?.[0] ||
    fallback
  );
}

/** A failed request, with its HTTP status (null when there was no response). */
export class RequestError extends Error {
  status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.status = status;
  }
}

async function requestError(response: Response | null, fallback: string) {
  return new RequestError(
    await readError(response, fallback),
    response?.status ?? null,
  );
}

export async function getJson<T>(url: string, fallback: string): Promise<T> {
  const response = await api.get(url);
  if (!response?.ok) throw await requestError(response, fallback);
  return response.json();
}

export async function send<T>(
  method: 'post' | 'put' | 'patch',
  url: string,
  body: unknown,
  fallback: string,
): Promise<T> {
  const response = await api[method](url, body);
  if (!response?.ok) throw await requestError(response, fallback);
  return response.json();
}

function useKnowledgeKeys() {
  const lists = useScopedQueryKey('knowledge-questions');
  const detail = useScopedQueryKey('knowledge-question');
  const summary = useScopedQueryKey('knowledge-question-summary');
  const faqs = useScopedQueryKey('additional-faqs');
  const dashboard = useScopedQueryKey('dashboard');
  return useMemo(
    () => ({
      lists: lists as QueryKey,
      detail: (id: number) => [...detail, id] as QueryKey,
      detailAll: detail as QueryKey,
      summary: summary as QueryKey,
      faqs: faqs as QueryKey,
      dashboard: dashboard as QueryKey,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      JSON.stringify(lists),
      JSON.stringify(detail),
      JSON.stringify(summary),
      JSON.stringify(faqs),
      JSON.stringify(dashboard),
    ],
  );
}

/** Questions still waiting on the store: not answered, not dismissed. */
export function useOpenKnowledgeQuestions() {
  const keys = useKnowledgeKeys();
  return useQuery({
    queryKey: [...keys.lists, 'open'],
    queryFn: () =>
      getJson<PaginatedKnowledgeQuestions>(
        apiEndpoints.knowledgeQuestions({
          status: 'open',
          page_size: LIST_PAGE_SIZE,
        }),
        'Questions could not be loaded.',
      ),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
}

export function useKnowledgeQuestionSummary() {
  const keys = useKnowledgeKeys();
  return useQuery({
    queryKey: keys.summary,
    queryFn: () =>
      getJson<KnowledgeQuestionSummary>(
        apiEndpoints.knowledgeQuestionSummary(),
        'Counts could not be loaded.',
      ),
  });
}

export function useKnowledgeQuestion(id: number | null) {
  const keys = useKnowledgeKeys();
  return useQuery({
    queryKey: keys.detail(id ?? 0),
    queryFn: () =>
      getJson<KnowledgeQuestionDetail>(
        apiEndpoints.knowledgeQuestionDetail(id as number),
        'This question could not be loaded.',
      ),
    enabled: id != null,
    refetchInterval: (query) =>
      isChecking(query.state.data?.faq) ? CHECK_POLL_MS : false,
    staleTime: 0,
  });
}

export function useAdditionalFaqs() {
  const keys = useKnowledgeKeys();
  return useQuery({
    queryKey: keys.faqs,
    queryFn: () =>
      getJson<AdditionalFaqList>(
        apiEndpoints.additionalFaqs('active'),
        'Additional FAQs could not be loaded.',
      ),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (data.results.some(isChecking)) return CHECK_POLL_MS;
      const sync = data.sync?.status;
      return sync === 'pending' || sync === 'syncing' ? 10_000 : false;
    },
    staleTime: 0,
  });
}

export function useKnowledgeBaseView() {
  const key = useScopedQueryKey('knowledge-base-view');
  return useQuery({
    queryKey: key,
    queryFn: () =>
      getJson<KnowledgeBaseView>(
        apiEndpoints.knowledgeBaseView(),
        'What your agent knows could not be loaded.',
      ),
  });
}

export function useKnowledgeActions() {
  const queryClient = useQueryClient();
  const keys = useKnowledgeKeys();

  const refreshAll = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.lists }),
        queryClient.invalidateQueries({ queryKey: keys.summary }),
        queryClient.invalidateQueries({ queryKey: keys.detailAll }),
        queryClient.invalidateQueries({ queryKey: keys.faqs }),
        queryClient.invalidateQueries({ queryKey: keys.dashboard }),
      ]),
    [queryClient, keys],
  );

  const setDetail = useCallback(
    async (detail: KnowledgeQuestionDetail) => {
      await queryClient.cancelQueries({ queryKey: keys.detail(detail.id) });
      queryClient.setQueryData(keys.detail(detail.id), detail);
      await refreshAll();
      return detail;
    },
    [queryClient, keys, refreshAll],
  );

  /** A changed FAQ also changes the question that links to it. */
  const afterFaq = useCallback(
    async (faq: AdditionalFaq) => {
      if (faq.source_gap_id != null) {
        queryClient.setQueryData<KnowledgeQuestionDetail>(
          keys.detail(faq.source_gap_id),
          (current) => current && { ...current, faq },
        );
      }
      queryClient.setQueryData<AdditionalFaqList>(
        keys.faqs,
        (current) =>
          current && {
            ...current,
            results: current.results.map((f) => (f.id === faq.id ? faq : f)),
          },
      );
      await refreshAll();
      return faq;
    },
    [queryClient, keys, refreshAll],
  );

  const dismiss = useCallback(
    async (id: number) =>
      setDetail(
        await send<KnowledgeQuestionDetail>(
          'post',
          apiEndpoints.knowledgeQuestionDismiss(id),
          {},
          'The question could not be dismissed.',
        ),
      ),
    [setDetail],
  );

  /** Creates or updates the question's FAQ draft. */
  const answer = useCallback(
    async (id: number, text: { question: string; answer: string }) =>
      setDetail(
        await send<KnowledgeQuestionDetail>(
          'put',
          apiEndpoints.knowledgeQuestionAnswer(id),
          text,
          'The answer could not be saved.',
        ),
      ),
    [setDetail],
  );

  const createFaq = useCallback(
    async (text: { question: string; answer: string }) => {
      const faq = await send<AdditionalFaq>(
        'post',
        apiEndpoints.additionalFaqs(),
        text,
        'The FAQ could not be added.',
      );
      await refreshAll();
      return faq;
    },
    [refreshAll],
  );

  const updateFaq = useCallback(
    async (id: number, text: { question: string; answer: string }) =>
      afterFaq(
        await send<AdditionalFaq>(
          'patch',
          apiEndpoints.additionalFaqDetail(id),
          text,
          'The FAQ could not be saved.',
        ),
      ),
    [afterFaq],
  );

  const checkFaq = useCallback(
    async (id: number) =>
      afterFaq(
        await send<AdditionalFaq>(
          'post',
          apiEndpoints.additionalFaqCheck(id),
          {},
          'The conflict check could not be started.',
        ),
      ),
    [afterFaq],
  );

  const publishFaq = useCallback(
    async (id: number) =>
      afterFaq(
        await send<AdditionalFaq>(
          'post',
          apiEndpoints.additionalFaqPublish(id),
          {},
          'The FAQ could not be published.',
        ),
      ),
    [afterFaq],
  );

  const archiveFaq = useCallback(
    async (id: number) =>
      afterFaq(
        await send<AdditionalFaq>(
          'post',
          apiEndpoints.additionalFaqArchive(id),
          {},
          'The FAQ could not be removed.',
        ),
      ),
    [afterFaq],
  );

  const supportDraft = useCallback(
    async (id: number) =>
      (
        await getJson<{ message: string }>(
          apiEndpoints.additionalFaqSupportDraft(id),
          'The message could not be drafted.',
        )
      ).message,
    [],
  );

  const sendToSupport = useCallback(
    async (id: number, message: string) =>
      afterFaq(
        await send<AdditionalFaq>(
          'post',
          apiEndpoints.additionalFaqSupport(id),
          { message },
          'The message could not be sent to support.',
        ),
      ),
    [afterFaq],
  );

  return {
    dismiss,
    answer,
    createFaq,
    updateFaq,
    checkFaq,
    publishFaq,
    archiveFaq,
    supportDraft,
    sendToSupport,
  };
}
